import axios from "axios";
import { useCallback } from "react";
import {
  REACT_APP_ACR_API_BASE_URL,
  REACT_APP_ACR_API_PROTOCOL,
} from "../utlities/core.constants";

const REPORT_API_BASE = `${REACT_APP_ACR_API_PROTOCOL}://${REACT_APP_ACR_API_BASE_URL}/api/report`;
const JSON_HEADERS = { headers: { "Content-Type": "application/json" } };

/**
 * Hook for ACR report API: create draft (POST), update (PUT), and complete (POST).
 * Callers pass DTOs shaped for the backend (createReportRequest / updateReportRequest).
 */
export default function useReport() {
  const createReport = useCallback(async (payload) => {
    return axios.post(REPORT_API_BASE, payload, JSON_HEADERS);
  }, []);

  const updateReport = useCallback(async (reportId, payload) => {
    return axios.put(`${REPORT_API_BASE}/${reportId}`, payload, JSON_HEADERS);
  }, []);

  /** Marks a draft report as completed (no request body). */
  const completeReport = useCallback(async (reportId) => {
    return axios.post(`${REPORT_API_BASE}/${reportId}/complete`);
  }, []);

  /** Soft-deletes a report (server enforces creator/admin ownership). */
  const deleteReport = useCallback(async (reportId) => {
    return axios.delete(`${REPORT_API_BASE}/${reportId}`);
  }, []);

  /** Returns reports for a product (caller uses at most the first item). */
  const fetchReportsByProduct = useCallback(async (productId) => {
    const response = await axios.get(`${REPORT_API_BASE}/all`, {
      params: { productId },
    });
    const data = response?.data;
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.content)) return data.content;
    return [];
  }, []);

  /**
   * Returns the authenticated caller's own reports (their drafts/ACRs). Ownership is enforced
   * server-side by the /mine endpoint from the caller's token, so no org id is sent from the client.
   */
  const fetchMyReports = useCallback(async ({ status, pageSize, pageNumber } = {}) => {
    const params = {};
    if (status) params.status = status;
    if (pageSize) params.pageSize = pageSize;
    if (pageNumber) params.pageNumber = pageNumber;
    const response = await axios.get(`${REPORT_API_BASE}/mine`, { params });
    const data = response?.data;
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.content)) return data.content;
    return [];
  }, []);

  return { createReport, updateReport, completeReport, deleteReport, fetchReportsByProduct, fetchMyReports };
}
